export const useAllSponsorsStore = defineStore('allSponsors', () => {
	const allSponsors = ref<Sponsor[]>([])

	const isGetLoading = ref(false)
	const isGetError = ref(false)

	const isCreateLoading = ref(false)
	const isCreateError = ref(false)
	const isCreateSuccess = ref(false)

	const isUpdateLoading = ref(false)
	const isUpdateError = ref(false)
	const isUpdateSuccess = ref(false)

	const isDeleteLoading = ref(false)
	const isDeleteError = ref(false)
	const isDeleteSuccess = ref(false)

	// Sets the sponsors
	const setSponsors = (newSponsors: Sponsor[]) => {
		allSponsors.value = newSponsors
	}

	// Gets all sponsors
	const getAllSponsors = async () => {
		// Set the loading state
		isGetLoading.value = true

		const { data, error } = await useFetch<PaginatedResponse<Sponsor>>(
			'/api/v1/sponsors',
			{
				method: 'GET',
				params: {
					page: 1,
					limit: -1,
				},
			}
		)
		// Get the sponsors from the API with AXIOS
		if (data.value) {
			const newSponsors = data.value.data as Sponsor[]
			setSponsors(newSponsors)

			isGetError.value = false
			isGetLoading.value = false
		}

		if (error.value) {
			console.log('error of getting sponsors: ', error.value)
			isGetError.value = true
			isGetLoading.value = false
		}
	}

	// Gets a sponsor by its id
	async function getSponsorById(id: number) {
		// Check if the sponsor array is empty
		if (allSponsors.value.length === 0) {
			// The get one sponsor endpoint is not implemented yet, but the number of sponsors is small, so it doesn't matter at the moment
			await getAllSponsors()
		}
		// Check if the sponsor is already in the store
		return allSponsors.value.find((sponsor) => sponsor.id === id)
	}

	// Creates a new sponsor
	const createSponsor = async (
		name: string,
		description: string,
		logoUrl: string
	) => {
		// Set the loading state
		isCreateLoading.value = true

		// Create the sponsor with useFetch
		const { data, error } = await useFetch<Sponsor>('/api/v1/sponsors', {
			method: 'POST',
			body: {
				name: name,
				description: description,
				logoUrl: logoUrl,
			},
		})

		if (error.value) {
			console.log('error of creating sponsor: ', error.value)
			isCreateError.value = true
			isCreateSuccess.value = false

			setTimeout(() => {
				isCreateError.value = false
			}, 6000)
		}

		if (data.value) {
			console.log('response of creating sponsor: ', data.value)
			const newSponsor = data.value as Sponsor
			allSponsors.value?.push(newSponsor)

			isCreateError.value = false
			isCreateSuccess.value = true

			setTimeout(() => {
				isCreateSuccess.value = false
			}, 6000)
		}

		isCreateLoading.value = false
	}

	// Updates a sponsor
	const updateSponsor = async (
		name: string,
		description: string,
		logoUrl: string,
		sponsorID: number
	) => {
		// Set the loading state
		isUpdateLoading.value = true

		const { data, error } = await useFetch<Sponsor>(
			`/api/v1/sponsors/${sponsorID}`,
			{
				method: 'PUT',
				body: {
					name: name,
					description: description,
					logoUrl: logoUrl,
				},
			}
		)

		if (error.value) {
			console.log('error of updating sponsor: ', error.value)
			isUpdateError.value = true
			isUpdateSuccess.value = false

			setTimeout(() => {
				isUpdateError.value = false
			}, 6000)
		}

		if (data.value) {
			console.log('response of updating sponsor: ', data.value)
			const newSponsors = allSponsors.value?.map((sponsor) => {
				if (sponsor.id === sponsorID) {
					sponsor.name = name
					sponsor.description = description
					sponsor.logoUrl = logoUrl
				}
				return sponsor
			})
			setSponsors(newSponsors as Sponsor[])

			isUpdateError.value = false
			isUpdateSuccess.value = true

			setTimeout(() => {
				isUpdateSuccess.value = false
			}, 6000)
		}

		isUpdateLoading.value = false
	}

	// Uploads the sponsor image
	async function uploadSponsorImage(file?: File) {
		if (!file)
			throw createError({ statusCode: 400, statusMessage: 'No file provided' })

		const formData = new FormData()
		formData.append('file', file)

		const response = await $fetch<PropellerImage>('/api/v1/image/upload', {
			method: 'POST',
			body: formData,
			query: {
				path: '/sponsors',
			},
		})
		return response
	}

	// Deletes a sponsor
	const deleteSponsor = async (id: number) => {
		// Set the loading state
		isDeleteLoading.value = true

		const { data, error } = await useFetch<Sponsor>(`/api/v1/sponsors/${id}`, {
			method: 'DELETE',
		})

		if (error.value) {
			console.log('error of deleting sponsor: ', error.value)
			isDeleteError.value = true
			isDeleteSuccess.value = false

			setTimeout(() => {
				isDeleteError.value = false
			}, 6000)
		}

		if (data.value) {
			console.log('response of deleting sponsor: ', data.value)
			// Delete the sponsor from the array also
			const newSponsors = allSponsors.value?.filter(
				(sponsor) => sponsor.id !== id
			)
			setSponsors(newSponsors as Sponsor[])

			isDeleteError.value = false
			isDeleteSuccess.value = true

			setTimeout(() => {
				isDeleteSuccess.value = false
			}, 6000)
		}

		isDeleteLoading.value = false
	}

	return {
		allSponsors,
		isGetLoading,
		isGetError,
		getAllSponsors,
		getSponsorById,
		isCreateLoading,
		isCreateError,
		isCreateSuccess,
		createSponsor,
		isUpdateLoading,
		isUpdateError,
		isUpdateSuccess,
		updateSponsor,
		uploadSponsorImage,
		isDeleteLoading,
		isDeleteError,
		isDeleteSuccess,
		deleteSponsor,
	}
})
